// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * API Client — composed from domain-specific modules.
 *
 * Domain modules (each independently importable for type reference):
 *   apiBase.ts      — axios instance, interceptors, auth helpers
 *   apiAuth.ts      — auth & sessions
 *   apiChat.ts      — chat runtime, thread history, and UI action APIs
 *   apiConfig.ts    — config, settings, model config/registry, versioning, workflow metadata
 *   apiKb.ts        — knowledge base & retrieval
 *   apiAdmin.ts     — admin dashboard, users, usage, sessions, system
 *   apiMisc.ts      — files, health, snapshots, about/dev
 *
 * All 25+ consumer files import `{ api }` from this barrel — no changes needed.
 */

import { ApiBase } from './apiBase';
import { ApiAuth } from './apiAuth';
import { ApiWorkspaces } from './apiWorkspaces';
import { ApiLearning } from './apiLearning';
import { ApiChat } from './apiChat';
import { ApiConfig } from './apiConfig';
import { ApiKb } from './apiKb';
import { ApiAdmin } from './apiAdmin';
import { ApiMemories } from './apiMemories';
import { ApiTeacherTemplates } from './apiTeacherTemplates';
import { ApiMisc } from './apiMisc';
import { ApiCommandSurface } from './apiCommandSurface';
import { ApiSuggestions } from './apiSuggestions';

// Re-export base helpers for consumers that may need them directly
export { API_BASE_URL, handleUnauthorized } from './apiBase';

// ---------------------------------------------------------------------------
// TypeScript mixin pattern — compose all domain classes into a single ApiClient
// ---------------------------------------------------------------------------

type Constructor<T = object> = new (...args: any[]) => T;

function applyMixins(derivedCtor: Constructor, constructors: Constructor[]) {
  for (const baseCtor of constructors) {
    for (const name of Object.getOwnPropertyNames(baseCtor.prototype)) {
      if (name === 'constructor') continue;
      Object.defineProperty(
        derivedCtor.prototype,
        name,
        Object.getOwnPropertyDescriptor(baseCtor.prototype, name) || Object.create(null)
      );
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class ApiClient extends ApiBase {}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface ApiClient
  extends
    ApiAuth,
    ApiWorkspaces,
    ApiLearning,
    ApiChat,
    ApiConfig,
    ApiKb,
    ApiAdmin,
    ApiMemories,
    ApiTeacherTemplates,
    ApiMisc,
    ApiCommandSurface,
    ApiSuggestions {}

applyMixins(ApiClient, [
  ApiAuth,
  ApiWorkspaces,
  ApiLearning,
  ApiChat,
  ApiConfig,
  ApiKb,
  ApiAdmin,
  ApiMemories,
  ApiTeacherTemplates,
  ApiMisc,
  ApiCommandSurface,
  ApiSuggestions,
]);

export const api = new ApiClient();
